import StyledTooltip from "@/components/styled-tooltip";

import { Info } from "lucide-react";
import { useMemo } from "react";
import RecommendationCard from "./recommendation-card";
import { recommendationList } from "./recommendation-list";
import {
  b2bProviders,
  b2cProviders,
  DetailedProviderBreakdownResponse,
  EmailProvider,
  PlacementType,
} from "@/types/inboxPlacements.types";
import { usePlacementContext } from "../inbox-placement-report/placement-context";
import { useGetProviderBreakdown } from "@/hooks/use-inboxplacement";

const percentageToCount = (percentage: number, total: number) => {
  return Math.round((percentage / 100) * total);
};

export const Recommendations = () => {
  const { state } = usePlacementContext();

  const { data: providerBreakdown, isLoading: providerBreakdownLoading } =
    useGetProviderBreakdown(state.params.email, state.params.testId);

  const recommendations = useMemo(() => {
    if (
      !providerBreakdown?.providers ||
      providerBreakdown?.providers.length === 0
    ) {
      return [];
    }

    const providersList =
      state.params.type === PlacementType.B2B ? b2bProviders : b2cProviders;

    const priorityMap = Object.fromEntries(
      providersList.map((provider, index) => [provider, index])
    );

    const summary = providerBreakdown?.providers
      .filter((provider) => {
        // Filter based on provider type (B2C/B2B)
        return providersList.includes(provider.provider as EmailProvider);
      })
      .sort((a, b) => {
        // Create priority map based on the providers list order

        // Sort based on the priority defined in providers list
        const priorityA = priorityMap[a.provider as EmailProvider] ?? 999;
        const priorityB = priorityMap[b.provider as EmailProvider] ?? 999;
        return priorityA - priorityB;
      })
      .map((provider) => {
        return {
          provider: provider.provider,
          inbox: percentageToCount(
            provider.inboxPercentage,
            provider.emailCount
          ),
          spam: percentageToCount(provider.spamPercentage, provider.emailCount),
          unreceived: percentageToCount(
            provider.unreceivedPercentage,
            provider.emailCount
          ),
          total: provider.emailCount || 0,
        };
      });

    const recommendations = [];

    for (const item of summary) {
      if (item.spam === 0) continue;

      const recommendation = recommendationList.find(
        (recommendation) =>
          recommendation.provider === item.provider &&
          recommendation.type === "hit-spam-folder"
      );

      if (recommendation) {
        recommendations.push(recommendation);
      }
    }

    return recommendations;
  }, [providerBreakdown, state.params.type]);

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-medium text-foreground">
          InboxDoctor&apos;s Insights
        </h2>
        <StyledTooltip description="InboxDoctor's power insights to help you with the resolve your email deliverability issues.">
          <Info className="h-4 w-4 text-muted-foreground" />
        </StyledTooltip>
        {/* <MoreInfoButton slug="email-placement-by-provider-type" /> */}
      </div>
      <div className="space-y-4 w-full">
        {recommendations.map((recommendation, index) => (
          <RecommendationCard
            key={`${recommendation.type}-${recommendation.provider}-${index}`}
            title={recommendation.title}
            iconUrl={recommendation.iconUrl}
            links={recommendation.links}
            content={recommendation.content()}
          />
        ))}
      </div>
    </div>
  );
};
